import { Component, DestroyRef, inject, OnInit, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
	ListLayoutComponent,
	TitleGroupComponent,
	ButtonComponent,
	AlertComponent,
	ToolbarComponent,
	TableComponent,
	IToolbar,
	ITableHeader,
	ITableConfig,
	ITableRow,
	ITableCellEmitter,
	ITableSortEvent,
	TableFilterComponent,
} from '@tecalisdev/infinity';
import { Observable } from 'rxjs';
import { ToolbarHelperService } from '../../../../../shared/services/toolbar-helper.service';
import { UsersHelperService } from '../../services/users-helper.service';
import { UsersService } from '../../services/users.service';
import { LoadingService } from '../../../../../shared/services/loading.service';
import { IUsersListBody, IUsersListResponse } from '../../models/users.models';
import { HttpErrorResponse } from '@angular/common/http';
import { formatFilterTable, transformTrimObject } from '../../../../../core/utils/utils';
import { Permission } from '../../../../../core/models/role-permissions.models';
import { RolePermissionsService } from '../../../../../core/services/role-permissions.service';
import { environment } from '../../../../../../environments/environment';
import { buildCurlPostJson } from '../../../../../panel/utils/build-curl.util';
import { ModalCurlRequestComponent } from '../../../../../shared/components/modal-curl-request/modal-curl-request.component';
import { PaginatedListController } from '../../../../../shared/list/paginated-list.controller';
import { MenuItemAction, RowItemAction } from '../../../../../shared/models/master.models';
import { UsersFormComponent } from '../users-form/users-form.component';
import {
	clearActivityUuidQueryParam,
	parseActivityDeepLinkUuid,
} from '../../../../../core/utils/settings-activity-deep-link.util';

@Component({
	selector: 'app-users-list',
	imports: [
		ListLayoutComponent,
		TitleGroupComponent,
		TranslatePipe,
		ButtonComponent,
		AlertComponent,
		ToolbarComponent,
		TableComponent,
		UsersFormComponent,
		ModalCurlRequestComponent,
		TableFilterComponent,
	],
	templateUrl: './users-list.component.html',
	styleUrl: './users-list.component.scss',
})
export class UsersListComponent extends PaginatedListController implements OnInit {
	private toolbarHelperService = inject(ToolbarHelperService);
	private route = inject(ActivatedRoute);
	private router = inject(Router);
	private usersHelper = inject(UsersHelperService);
	private usersService = inject(UsersService);
	private loading = inject(LoadingService);
	public rolePermissions = inject(RolePermissionsService);
	private trans = inject(TranslateService);
	private destroyRef = inject(DestroyRef);

	protected readonly Permission = Permission;

	// TABLE
	public toolbar: Signal<IToolbar>;
	public header: ITableHeader[] = this.usersHelper.header;
	public override config = this.usersHelper.config as ITableConfig;
	public tableFilters: any;
	private sort: ITableSortEvent = this.usersHelper.sort;
	public content: ITableRow[] = [];
	private dataContent: IUsersListBody[];

	// GENERAL
	public showForm: boolean = false;
	public userSelected: IUsersListBody;
	public readonly curlModalOpen = signal(false);
	private pendingActivityUuid: string | null = null;
	private deepLinkSearchRetried = false;

	ngOnInit(): void {
		this.pendingActivityUuid = parseActivityDeepLinkUuid(this.route);
		this.toolbarHelperService.initializeListToolbar(this, () => this.usersHelper.toolbar);

		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.header = this.usersHelper.header;
			this.config = { ...this.usersHelper.config, pagination: this.config.pagination };
		});
	}

	protected override beforeListRequest(): void {
		delete this.config.status;
		this.loading.showTable(this.config, this.header.length);
	}

	protected override afterListRequest(): void {
		this.loading.hideTable(this.config);
	}

	protected createListRequest(): Observable<IUsersListResponse> {
		this.getPayload();
		return this.usersService.list(this.tableFilters);
	}

	protected applyListResponse(response: IUsersListResponse): void {
		this.dataContent = response.data;
		this.content = this.usersHelper.transformRow(response.data);
		this.tryOpenUserFromActivityDeepLink();
	}

	protected override onListError(_error: HttpErrorResponse): void {
		this.config['status'] = { type: 'error' };
	}

	private getPayload(): void {
		this.tableFilters = this.withPagination(this.tableFilters, {
			sort_by: this.sort.sortKey,
			sort_order: this.sort.sortDirection,
		});
		this.toolbarHelperService.cleanSavedToolbar(this.constructor.name);
	}

	public tableCell(cell: ITableCellEmitter): void {
		const actions = {
			[RowItemAction.EDIT]: () => {
				const row: IUsersListBody = this.dataContent.find((row: IUsersListBody) => row.uuid === cell.rowId);
				if (this.usersHelper.canShowEditAction(row)) {
					this.showForm = true;
					this.userSelected = row;
				}
			},
		};

		const actionId = cell?.cellId?.id || cell?.cellId;
		actions[actionId] ? actions[actionId]() : null;
	}

	public sortChange(event: ITableSortEvent): void {
		if (!event.sortDirection) this.sort = this.usersHelper.resetSort();
		else this.sort = event;

		this.getList();
	}

	public override filterChange(filters: any): void {
		this.tableFilters = transformTrimObject(filters);
		this.setListConfigPagination(
			this.hasPrevPagination
				? { current_page: this.config?.pagination?.page, total: this.config?.pagination?.total }
				: { current_page: 1 },
		);
		this.hasPrevPagination = false;
		this.getList();
	}

	public getUsersListCurl(): string {
		if (!this.tableFilters) return '';
		return buildCurlPostJson(environment.apiUrl, '/users', this.tableFilters as object);
	}

	public openCurlModal(): void {
		this.curlModalOpen.set(true);
	}

	public closeCurlModal(): void {
		this.curlModalOpen.set(false);
	}

	public onToolbarAction(action: string | { id?: string }): void {
		const id = typeof action === 'string' ? action : action?.id;
		if (id === MenuItemAction.SHOW_CURL) {
			this.openCurlModal();
		}
	}

	public goToManage(): void {
		this.usersHelper.openAccountManageUsers();
	}

	public closeForm(reload: boolean): void {
		if (reload) this.getList();
		this.showForm = false;
	}

	private tryOpenUserFromActivityDeepLink(): void {
		const uuid = this.pendingActivityUuid;
		if (!uuid || !this.rolePermissions.canEditUsers()) {
			this.finishActivityDeepLinkIfMatched(uuid);
			return;
		}
		const row = this.dataContent?.find((r: IUsersListBody) => r.uuid === uuid);
		if (row) {
			if (this.usersHelper.canShowEditAction(row)) {
				this.showForm = true;
				this.userSelected = row;
			}
			this.finishActivityDeepLinkIfMatched(uuid);
			return;
		}
		if (!this.deepLinkSearchRetried) {
			this.deepLinkSearchRetried = true;
			this.tableFilters = {
				...formatFilterTable(this.tableFilters || {}),
				name: uuid,
				page: 1,
			};
			this.config.pagination.page = 1;
			this.getList();
			return;
		}
		this.finishActivityDeepLinkIfMatched(uuid);
	}

	private finishActivityDeepLinkIfMatched(expectedUuid: string | null): void {
		if (!expectedUuid || this.pendingActivityUuid !== expectedUuid) {
			return;
		}
		this.pendingActivityUuid = null;
		this.deepLinkSearchRetried = false;
		clearActivityUuidQueryParam(this.router, this.route);
	}
}
